import { useLayoutEffect, useRef } from "react";
import { gsap } from "../utils/gsap";

const LatestWorkPricing = () => {
  const sectionRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const prefersReduced =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) return;

    const ctx = gsap.context(() => {
      const left = section.querySelector(".latest-work__pricing-left");
      const accents = gsap.utils.toArray(
        ".latest-work__pricing-bespoke",
        section
      );
      const cards = gsap.utils.toArray(".pricing-card", section);
      const cardHeaders = gsap.utils.toArray(".pricing-card__header", section);
      const cardFeatures = gsap.utils.toArray(
        ".pricing-card__features li",
        section
      );
      const cardCtas = gsap.utils.toArray(".pricing-card__cta", section);

      gsap.set([left, ...accents], {
        autoAlpha: 0,
        y: 24,
        filter: "blur(6px)",
      });
      gsap.set(cards, {
        autoAlpha: 0,
        y: 36,
        scale: 0.98,
        filter: "blur(8px)",
      });

      gsap
        .timeline({
          scrollTrigger: {
            trigger: section,
            start: "top 80%",
            end: "bottom 30%",
          },
        })
        .to(left, {
          autoAlpha: 1,
          y: 0,
          filter: "blur(0px)",
          duration: 0.7,
          ease: "power2.out",
        })
        .to(
          accents,
          {
            autoAlpha: 1,
            y: 0,
            filter: "blur(0px)",
            duration: 0.6,
            ease: "power2.out",
            stagger: 0.08,
          },
          "-=0.35"
        )
        .to(
          cards,
          {
            autoAlpha: 1,
            y: 0,
            scale: 1,
            filter: "blur(0px)",
            duration: 0.7,
            ease: "power3.out",
            stagger: 0.12,
          },
          "-=0.25"
        )
        .from(
          cardHeaders,
          {
            autoAlpha: 0,
            y: 18,
            duration: 0.5,
            ease: "power2.out",
            stagger: 0.08,
          },
          "-=0.5"
        )
        .from(
          cardFeatures,
          {
            autoAlpha: 0,
            x: -12,
            duration: 0.4,
            ease: "power2.out",
            stagger: 0.04,
          },
          "-=0.35"
        )
        .from(
          cardCtas,
          {
            autoAlpha: 0,
            y: 12,
            duration: 0.5,
            ease: "power2.out",
            stagger: 0.08,
          },
          "-=0.2"
        );
    }, section);

    return () => ctx.revert();
  }, []);

  return (
    <div ref={sectionRef} className="latest-work__pricing px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 2xl:px-32">
      <div className="latest-work__pricing-left">
        <h3 className="latest-work__pricing-title">Services</h3>
        <p className="latest-work__pricing-copy">
          Choose the perfect package for your project. Web, Mobile, or AI — we've got you covered.
        </p>
        <div className="latest-work__pricing-divider" />
        <div className="latest-work__pricing-bespoke">
          <div>
            <h4 className="latest-work__pricing-bespoke-title">Custom Solutions</h4>
            <p className="latest-work__pricing-bespoke-copy">
              Need something unique? Let's discuss your specific requirements.
            </p>
          </div>
          <a href="#tidycall" className="latest-work__pricing-contact">
            Contact us
          </a>
        </div>
      </div>

      <div className="latest-work__pricing-grid">
        {/* Starter Package */}
        <article className="pricing-card">
          <header className="pricing-card__header">
            <div>
              <h4 className="pricing-card__title">Starter</h4>
              <p className="pricing-card__subtitle">
                Perfect for solo founders and early-stage brands.
              </p>
            </div>
          </header>
          <div className="pricing-card__divider" />

          {/* Project Types */}
          <div className="pricing-card__types">
            <div className="pricing-card__type">
              <h5 className="pricing-card__type-title">🌐 Web</h5>
              <p className="pricing-card__type-desc">Landing pages, portfolios, business sites</p>
            </div>
            <div className="pricing-card__type">
              <h5 className="pricing-card__type-title">📱 Mobile</h5>
              <p className="pricing-card__type-desc">iOS & Android apps with core features</p>
            </div>
            <div className="pricing-card__type">
              <h5 className="pricing-card__type-title">🤖 AI</h5>
              <p className="pricing-card__type-desc">Basic automation & AI integrations</p>
            </div>
          </div>

          <ul className="pricing-card__features">
            <li>Premium Template Design</li>
            <li>Up to 3 Pages/Screens</li>
            <li>2 Rounds of Revisions</li>
            <li>Basic SEO Setup</li>
            <li>Fully Responsive</li>
            <li>Email Support</li>
          </ul>

          <a href="#tidycall" className="pricing-card__cta">
            Get Started
          </a>
        </article>

        {/* Custom Package */}
        <article className="pricing-card pricing-card--featured">
          <header className="pricing-card__header">
            <div>
              <h4 className="pricing-card__title">Custom</h4>
              <p className="pricing-card__subtitle">
                For scaling businesses ready to go deeper.
              </p>
            </div>
            <span className="pricing-card__tag">Popular</span>
          </header>
          <div className="pricing-card__divider" />

          {/* Project Types */}
          <div className="pricing-card__types">
            <div className="pricing-card__type">
              <h5 className="pricing-card__type-title">🌐 Web</h5>
              <p className="pricing-card__type-desc">Complex web apps, SaaS platforms, dashboards</p>
            </div>
            <div className="pricing-card__type">
              <h5 className="pricing-card__type-title">📱 Mobile</h5>
              <p className="pricing-card__type-desc">Full-featured native or cross-platform apps</p>
            </div>
            <div className="pricing-card__type">
              <h5 className="pricing-card__type-title">🤖 AI</h5>
              <p className="pricing-card__type-desc">Advanced AI solutions & custom models</p>
            </div>
          </div>

          <ul className="pricing-card__features">
            <li>100% Custom Design</li>
            <li>Unlimited Pages/Screens</li>
            <li>Unlimited Revisions</li>
            <li>Technical SEO & Speed Optimization</li>
            <li>Advanced Animations & Interactions</li>
            <li>Complex Functionality (CMS, APIs, Integrations)</li>
            <li>Priority WhatsApp & Email Support</li>
            <li>Post-Launch Support</li>
          </ul>

          <a href="#tidycall" className="pricing-card__cta">
            Let's Talk
          </a>
        </article>
      </div>
    </div>
  );
};

export default LatestWorkPricing;
